import type { Anime, AnimeInfo, Comment, Episode, HomePayload, NativeBackupState, NewsItem, StatusResult, User } from './types';

type Params = Record<string, string | number | undefined>;

function normalizeResult<T>(result?: T[] | T): T[] {
  if (Array.isArray(result)) return result;
  return result ? [result] : [];
}

function contentModes(): Params {
  return {
    cmode: localStorage.getItem('anime-cloud:children-mode') === 'true' ? '1' : '0',
    hiddenMode: localStorage.getItem('anime-cloud:hidden-mode') === 'true' ? '1' : '0'
  };
}

export async function api<T = unknown>(command: string, params: Params = {}): Promise<T> {
  const response = await fetch('/api/anime', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ command, ...params })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'تعذر الاتصال بالخادم');
  return data as T;
}

export const animeApi = {
  home: () => api<HomePayload>('getNewEpAndAnime', contentModes()),
  catalog: (command = 'getAllAnime', params: Params = {}) =>
    api<{ result: Anime[] }>(command, { ...contentModes(), ...params }).then((x) => x.result || []),
  animeDetails: (animeID: string) =>
    api<{ mainResult: AnimeInfo[]; result: Episode[]; SettingsResult?: Record<string, unknown> }>('getAnimeDetails', { animeID }),
  animeMore: (animeID: string) =>
    api<{ result: AnimeInfo[] }>('getAnimeMoreDetails', { animeID }).then((x) => x.result?.[0] || {}),
  related: (animeID: string, rootID = animeID) =>
    api<{ result: Anime[] }>('getRelatedAnime', { animeID, rootID, ...contentModes() }).then((x) => x.result || []),
  facets: (command: 'getGenres' | 'getYears' | 'getSeason' | 'getAges') =>
    api<{ result: Record<string, string>[] }>(command).then((x) => x.result || []),
  news: () => api<{ result: NewsItem[] }>('getNews').then((x) => x.result || []),
  newsDetails: (newsID: string) =>
    api<{ result: { content: string }[] }>('getNewsDetails', { newsID }).then((x) => x.result?.[0]?.content || ''),
  schedule: () => api<{ result: Anime[] }>('getAnimeWithDays', contentModes()).then((x) => x.result || []),
  trackAppVisit: () => api('updateAppVisiterCount'),
  trackAnimeVisit: (animeID: string) => api('updateAnimeVisiterCount', { animeID }),
  trackNewsVisit: (newsID: string) => api('updateNewsCount', { newsID }),
  comments: (epID: string, offset = 0, orderBy = '1') =>
    api<{ result: Comment[] }>('getComments', { epID, offset, orderBy }).then((x) => x.result || []),
  animeComments: (animeID: string, offset = 0, orderBy = '1') =>
    api<{ result: Comment[] }>('getAnimeComments', { animeID, offset, orderBy }).then((x) => x.result || []),
  commentRules: () => api<{ result: { status: string; title: string; message: string }[] }>('getCommentsRuls').then((x) => x.result?.[0]),
  video: async (episodeId: string, quality: 'hd' | 'sd') => {
    const response = await fetch(`/api/video/${encodeURIComponent(episodeId)}?quality=${quality}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'الفيديو غير متاح');
    return data as { url: string; note: string; quality: 'hd' | 'sd' };
  },
  login: async (email: string, password: string) => {
    const request = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const response = await request.json().catch(() => ({})) as { result?: User[] | User; status?: string; error?: string };
    if (!request.ok) throw new Error(response.error || 'تعذر تسجيل الدخول');
    return {
      ...response,
      result: normalizeResult(response.result).map((user) => ({ ...user, userid: user.userid || user.id || '' }))
    };
  },
  signup: (username: string, email: string, password: string) =>
    api<{ result?: StatusResult[] | StatusResult }>('userSignup', { username, email, password, token: '' }).then((data) => ({ ...data, result: normalizeResult(data.result) })),
  activate: (code: string, email: string, password: string) =>
    api<{ result?: StatusResult[] | StatusResult }>('activateUserAccount', { code, email, password }).then((data) => ({ ...data, result: normalizeResult(data.result) })),
  recover: (email: string) => api<{ result?: StatusResult[] | StatusResult }>('recoverPassword', { email }).then((data) => ({ ...data, result: normalizeResult(data.result) })),
  subscribeDays: (user: User) => api<{ result?: StatusResult[] | StatusResult }>('getSubscribeDays', { userID: user.userid, uniqID: user.uniqid }).then((data) => ({ ...data, result: normalizeResult(data.result) })),
  logout: async () => {
    const response = await fetch('/api/auth/logout', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
    if (!response.ok) throw new Error('تعذر تسجيل الخروج من الخادم');
  },
  session: async () => {
    const response = await fetch('/api/auth/session');
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'انتهت جلسة الحساب');
    return data.user as User;
  },
  addComment: (params: Params) => api('addComment', params),
  reactToComment: (command: 'addlike' | 'addDisLike', commentID: string, user?: User) =>
    api(command, { commentID, userID: user?.userid, uniqID: user?.uniqid }),
  reportComment: (commentID: string, user?: User) =>
    api('reportComment', { commentID, userID: user?.userid, uniqID: user?.uniqid }),
  updateComment: (commentID: string, content: string, epName: string, animeName: string, user: User) =>
    api('updateComment', { commentID, content, epName, animeName, userID: user.userid, uniqID: user.uniqid }),
  deleteComment: (commentID: string, user: User) =>
    api('deleteComment', { commentID, userID: user.userid, uniqID: user.uniqid }),
  changeUsername: (newUsername: string, user: User) =>
    api<{ result?: StatusResult[] }>('changeUsername', { userID: user.userid, uniqID: user.uniqid, newUsername, oldUsername: user.username }),
  changePassword: (oldPassword: string, newPassword: string, user: User) =>
    api<{ result?: StatusResult[] }>('changeUserPassword', { userID: user.userid, uniqID: user.uniqid, oldPassword, newPassword }),
  uploadProfile: async (file: File) => {
    const form = new FormData();
    form.append('command', 'uploadProfilePicture');
    form.append('file', file, file.name || 'profile.jpeg');
    const response = await fetch('/api/account-upload/profile', { method: 'POST', body: form });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'تعذر رفع الصورة');
    return data as { result?: { imageUrl?: string; status?: string }[] | { imageUrl?: string; status?: string }; imageUrl?: string };
  },
  uploadNativeBackup: async (file: File) => {
    const form = new FormData();
    form.append('command', 'saveBackup');
    form.append('fileToUpload', file, 'animeDB.sqlite');
    const response = await fetch('/api/account-upload/backup', { method: 'POST', body: form });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'تعذر رفع النسخة السحابية');
    return data as { result?: StatusResult[] | StatusResult };
  },
  importNativeBackup: async (file: File) => {
    const form = new FormData();
    form.append('command', 'importNativeBackup');
    form.append('file', file, 'animeDB.sqlite');
    const response = await fetch('/api/native-backup/import', { method: 'POST', body: form });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'تعذر قراءة نسخة iPhone');
    return data.state as NativeBackupState;
  },
  restoreNativeCloudBackup: async () => {
    const response = await fetch('/api/account-backup/state');
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'تعذر استعادة النسخة السحابية');
    return data.state as NativeBackupState;
  }
};
